import { pipe, Either } from "fp";

/**
 * `Data.Remote<A>` represents any `A` which can be requested and received from
 * some remote resource
 */
export type Remote<A> =
  | { readonly remote: "idle" }
  | { readonly remote: "loading" }
  | { readonly remote: Either<Error, A> };

export namespace Remote {
  export const idle = <A>(): Remote<A> => ({ remote: "idle" });
  export const loading = <A>(): Remote<A> => ({ remote: "loading" });
  export const received = <A>(received: Either<Error, A>): Remote<A> => ({
    remote: received,
  });

  export const success = <A>(success: A): Remote<A> =>
    received(Either.right(success));

  export const error = <A>(error: Error): Remote<A> =>
    received(Either.left(error));

  export const isLoading = <A>({ remote }: Remote<A>): boolean =>
    remote === "loading";

  export const equals = <A>(equals: (a: A) => (b: A) => boolean) => (
    a: Remote<A>
  ) => (b: Remote<A>): boolean => {
    if (a.remote === b.remote) return true;
    if (typeof a.remote === "string" || typeof b.remote === "string")
      return false;

    if (Either.isLeft(a.remote) && Either.isLeft(b.remote)) return true;
    return (
      Either.isRight(a.remote) &&
      Either.isRight(b.remote) &&
      equals(a.remote.right)(b.remote.right)
    );
  };

  export const map = <A, B>(fn: (a: A) => B) => ({
    remote,
  }: Remote<A>): Remote<B> => ({
    remote: typeof remote === "string" ? remote : Either.map(fn)(remote),
  });

  export const getOrElse = <A>(orElse: () => A) => (remote: Remote<A>): A =>
    pipe(
      remote,
      when({
        idle: orElse,
        loading: orElse,
        error: orElse,
        success: (success) => success,
      })
    );

  export type When<A, B> = {
    readonly idle?: () => B;
    readonly loading: () => B;
    readonly error: (error: Error) => B;
    readonly success: (a: A) => B;
  };

  /**
   * Convert `Data.Remote<A>` to a `B`, which requires handling each case of the
   * data structure, i.e. `idle`, `loading`, or `received`...
   *
   * ```ts
   * pipe(
   *   users,
   *   Data.Remote.when({
   *     idle: () => <>Select a user to search for.</>,
   *     loading: () => <>Loading...</>,
   *     error: error => <>Something went wrong! {error.message}</>,
   *     success: users => <div>Found {users.length} results...</div>
   *   })
   * ) ===
   *   Data.Remote.when(users, {
   *     idle: () => <>Select a user to search for.</>,
   *     loading: () => <>Loading...</>,
   *     error: error => <>Something went wrong! {error.message}</>,
   *     success: users => <div>Found {users.length} results...</div>
   *   })
   * ```
   */
  export function when<A, B>(when: When<A, B>): (remote: Remote<A>) => B;
  export function when<A, B>(remote: Remote<A>, when: When<A, B>): B;
  export function when<A, B>(
    whenOrRemote: Remote<A> | When<A, B>,
    whenOrUndefined?: When<A, B>
  ): B | ((remote: Remote<A>) => B) {
    if (whenOrUndefined)
      return when(whenOrUndefined)(whenOrRemote as Remote<A>);

    const { loading, idle = loading, error, success } = whenOrRemote as When<
      A,
      B
    >;

    return ({ remote }: Remote<A>): B => {
      switch (remote) {
        case "idle":
          return idle();
        case "loading":
          return loading();
        default:
          return Either.fold(error, success)(remote);
      }
    };
  }
}
